import { type KeyboardEvent, useCallback, useId, useRef, useState } from 'react';
import { Button } from '@/ui/shadcn/button';
import { Input } from '@/ui/shadcn/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/ui/shadcn/popover';
import type { ComboboxOption } from '../components/Combobox/Combobox';
import { selectPlaceholder } from '../fields/placeholders';
import { Icon } from '../icons/Icon';
import styles from './AddTagPicker.module.css';

/**
 * The *Add tag* combobox on shadcn's Input and Popover (AD #39; F1-e spec FR-P1, NFR-7; WI-1.4 plan
 * Decisions 7, 10 and 19), built as F1-f's account picker is — no `cmdk`, no new package. It takes the
 * place of the server-mode src/ui Combobox with `shortList`, and keeps what the section relied on:
 *
 * - **The input is the typed text**, held by the section (`inputValue`, `onInputChange`); empty, it shows
 *   *Select tag* (`selectPlaceholder`); it is `aria-busy` while the options load. A pick calls `onPick` with the option's id and closes the list;
 *   the section clears the text.
 * - **Keyboard — the ARIA 1.2 combobox with a listbox popup.** Typing opens the list, as a click on the input does; ArrowDown opens it
 *   and moves the active option down, ArrowUp up (`aria-activedescendant`, no DOM focus move); Enter
 *   picks the active option. Escape closes the list: the Popover is then the highest Radix layer, so it
 *   takes the Escape and a dialog around it does not. Focus never leaves the input — the Popover takes
 *   no focus, and a press on an option or on *Show suggestions* keeps it.
 * - **A short list never scrolls** (BUG-123): the section passes a bounded number of options, the list
 *   is as tall as they are, and what it left out is `moreMessage` — a line under the list that the list
 *   names as its description, said politely while the list is open by a live region outside the Popover.
 * - **With no options** the list's one line is `emptyMessage` (*Loading account tags*, *No more account
 *   tags*, *No matching account tags*), as a disabled option, so the listbox is never empty.
 * - The matched part of each option is emphasised (design-system §5 Combobox).
 * - An option's `detail`, when it has one, is a second line under its label — a colleague's role in the *Access*
 *   dialog's search.
 *
 * Test ids: the input takes `data-testid`; *Show suggestions* `<id>-trigger`, each option
 * `<id>-option-<option id>`, the live region `<id>-more-live` — the src/ui Combobox's — and the empty
 * line `<id>-empty`.
 */

export interface AddTagPickerProps {
    readonly options: readonly ComboboxOption[];
    readonly inputValue: string;
    readonly onInputChange: (text: string) => void;
    readonly onPick: (id: string) => void;
    /** The options are loading: the input is `aria-busy`. */
    readonly busy: boolean;
    readonly emptyMessage: string;
    /** What the bounded list left out, or `undefined`. */
    readonly moreMessage: string | undefined;
    readonly 'data-testid': string;
    /** The input's accessible name, when no visible label names it (`AddTagControl`). */
    readonly 'aria-label'?: string;
    /** The input's placeholder; *Select tag* when not given. */
    readonly placeholder?: string;
    /** Open the list as the picker mounts — `AddTagControl` shows it as the chip is pressed. */
    readonly defaultOpen?: boolean;
    /** `Escape` with the list already closed: `AddTagControl` folds back to its chip. */
    readonly onDismiss?: () => void;
    /**
     * The input sits in a form that another control submits (`SearchSelect` in the Edit user dialog): `Enter`
     * never reaches the form. It picks the active option, else the one option the typed text leaves, else
     * nothing — so pressing `Enter` after typing never saves the form with the old choice (L-198).
     */
    readonly holdEnter?: boolean;
    /** The listbox's accessible name; *Tags* when not given. A caller that lists something else names it (BUG-295). */
    readonly listLabel?: string;
}

/** The label split around the first case-insensitive match of the trimmed text. */
function matchParts(label: string, text: string) {
    const typed = text.trim();
    const at = typed === '' ? -1 : label.toLowerCase().indexOf(typed.toLowerCase());
    if (at < 0) {
        return null;
    }
    return {
        before: label.slice(0, at),
        match: label.slice(at, at + typed.length),
        after: label.slice(at + typed.length),
    };
}

export function AddTagPicker({
    options,
    inputValue,
    onInputChange,
    onPick,
    busy,
    emptyMessage,
    moreMessage,
    'data-testid': testId,
    'aria-label': ariaLabel,
    placeholder = selectPlaceholder('Tag'),
    defaultOpen = false,
    onDismiss,
    holdEnter = false,
    listLabel = 'Tags',
}: AddTagPickerProps) {
    const listId = useId();
    const moreId = `${listId}-more`;
    const anchorRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(defaultOpen);
    /** Set by the Popover's own `Escape`, which runs before the input's key handler sees the same key. */
    const escapeClosedList = useRef(false);
    const [active, setActive] = useState(-1);
    const activeIndex = open && active < options.length ? active : -1;
    const optionId = (id: string) => `${listId}-option-${id}`;
    const activeOption = activeIndex === -1 ? undefined : options[activeIndex];

    // `aria-activedescendant` moves no DOM focus, so the active option is scrolled into view here — a
    // callback ref, called when the option attaches (the Popover's portal mounts a render after it opens).
    const scrollActiveIntoView = useCallback((element: HTMLDivElement | null) => {
        element?.scrollIntoView({ block: 'nearest' });
    }, []);

    const show = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setActive(-1);
        }
    };

    const pick = (option: ComboboxOption) => {
        show(false);
        onPick(option.id);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                setOpen(true);
                setActive(options.length === 0 ? -1 : Math.min(activeIndex + 1, options.length - 1));
                return;
            }
            case 'ArrowUp': {
                event.preventDefault();
                setOpen(true);
                // From no active option, ArrowUp starts at the last one.
                setActive(
                    options.length === 0
                        ? -1
                        : activeIndex === -1
                          ? options.length - 1
                          : Math.max(activeIndex - 1, 0),
                );
                return;
            }
            case 'Escape': {
                if (escapeClosedList.current) {
                    escapeClosedList.current = false;
                } else {
                    onDismiss?.();
                }
                return;
            }
            case 'Enter': {
                if (activeOption !== undefined) {
                    event.preventDefault();
                    pick(activeOption);
                    return;
                }
                if (holdEnter) {
                    event.preventDefault();
                    const only = inputValue.trim() === '' || options.length !== 1 ? undefined : options[0];
                    if (only !== undefined) {
                        pick(only);
                    }
                }
                return;
            }
            default:
                return;
        }
    };

    return (
        <div className={styles.picker}>
            <Popover onOpenChange={show} open={open}>
                <PopoverAnchor asChild>
                    <div className={styles.shell} ref={anchorRef}>
                        <Input
                            aria-activedescendant={
                                activeOption === undefined ? undefined : optionId(activeOption.id)
                            }
                            aria-autocomplete="list"
                            aria-label={ariaLabel}
                            aria-busy={busy}
                            aria-controls={open ? listId : undefined}
                            aria-expanded={open}
                            autoComplete="off"
                            data-testid={testId}
                            onChange={(event) => {
                                onInputChange(event.target.value);
                                setOpen(true);
                                setActive(-1);
                            }}
                            onClick={() => setOpen(true)}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            role="combobox"
                            value={inputValue}
                        />
                        <Button
                            aria-label="Show suggestions"
                            data-testid={`${testId}-trigger`}
                            onClick={() => show(!open)}
                            // Keeps focus in the input, which owns the keyboard.
                            onMouseDown={(event) => event.preventDefault()}
                            size="icon-sm"
                            tabIndex={-1}
                            variant="ghost"
                        >
                            <Icon name="expand" size="sm" />
                        </Button>
                    </div>
                </PopoverAnchor>
                <PopoverContent
                    align="start"
                    className={styles.popover}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    onInteractOutside={(event) => {
                        // A press on the input or on Show suggestions is the combobox's own, not outside.
                        if (event.target instanceof Node && anchorRef.current?.contains(event.target)) {
                            event.preventDefault();
                        }
                    }}
                    onEscapeKeyDown={() => {
                        escapeClosedList.current = true;
                    }}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                >
                    {/* `div`s, not `ul` / `li`: Biome's a11y group refuses an interactive role on a list element. */}
                    <div
                        aria-label={listLabel}
                        className={styles.list}
                        id={listId}
                        role="listbox"
                        {...(moreMessage === undefined ? {} : { 'aria-describedby': moreId })}
                    >
                        {options.length === 0 ? (
                            <div
                                aria-disabled="true"
                                aria-selected="false"
                                className={styles.empty}
                                data-testid={`${testId}-empty`}
                                role="option"
                                tabIndex={-1}
                            >
                                {emptyMessage}
                            </div>
                        ) : (
                            options.map((option, index) => {
                                const parts = matchParts(option.label, inputValue);
                                return (
                                    // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard path is the combobox input's (ArrowDown, ArrowUp, Enter through aria-activedescendant); an option never takes focus.
                                    <div
                                        aria-selected={index === activeIndex}
                                        className={styles.option}
                                        data-testid={`${testId}-option-${option.id}`}
                                        id={optionId(option.id)}
                                        key={option.id}
                                        onClick={() => pick(option)}
                                        // Keeps focus in the input, so the pick is not a focus move out of it.
                                        onMouseDown={(event) => event.preventDefault()}
                                        ref={index === activeIndex ? scrollActiveIntoView : undefined}
                                        role="option"
                                        // Reachable by pointer only; the keyboard reaches it through the
                                        // input's `aria-activedescendant`, so it stays out of the tab order.
                                        tabIndex={-1}
                                    >
                                        <span>
                                            {parts === null ? (
                                                option.label
                                            ) : (
                                                <>
                                                    {parts.before}
                                                    <span className={styles.match}>{parts.match}</span>
                                                    {parts.after}
                                                </>
                                            )}
                                        </span>
                                        {option.detail === undefined ? null : (
                                            <span className={styles.detail}>{option.detail}</span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                    {moreMessage === undefined ? null : (
                        <p className={styles.more} id={moreId}>
                            {moreMessage}
                        </p>
                    )}
                </PopoverContent>
            </Popover>
            {/* Outside the Popover and always rendered, so the line is announced as the list opens (BUG-123). */}
            <span aria-live="polite" className={styles.visuallyHidden} data-testid={`${testId}-more-live`}>
                {open ? (moreMessage ?? '') : ''}
            </span>
        </div>
    );
}
